import * as React from "react";

import type { IconSpec } from "@impero-com/common/componentTypes/icon";

/**
 * Interface for a button spec (an item in the button panel).
 */
interface ElementButtonSpec<ElementButtonSpecId extends string> {
    id: ElementButtonSpecId;
    icon: IconSpec;
    label: string;
    onClick?: () => void;
    renderPlaceholder: () => React.ReactElement;
}

type SectionSpecId = string;

/**
 * The list of functions that we need to be able to render a section.
 */
interface SectionAccessors<SEC, ELT> {
    getSectionId: (section: SEC) => SectionSpecId;
    getColCount: (section: SEC) => number;
    // Use columns and not rows because we need to ensure that all empty cells are at the bottom
    getElementColumns: (section: SEC) => Array<Array<ELT>>;
    renderSection: (
        section: SEC,
        children: React.ReactElement | null
    ) => React.ReactElement;
    renderSectionPlaceholder: (
        section: SEC,
        children: React.ReactElement | null
    ) => React.ReactElement | null;
}

type ElementSpecId = string;

/**
 * The list of functions that we need to be able to render an element.
 */
interface ElementAccessors<ELT> {
    getElementId: (elt: ELT) => ElementSpecId;
    renderElement: (elt: ELT) => React.ReactElement;
    renderElementPlaceholder: (elt: ELT) => React.ReactElement;
}

/**
 * Same as ElementAccessors, but without the placeholder rendering function. To render a placeholder
 * for an element in the React components implementation, we use a lambda function held in the state of ContentPanel.
 */
type InternalElementAccessors<ELT> = Omit<
    ElementAccessors<ELT>,
    "renderElementPlaceholder"
>;

/**
 * Grid location (within a section) of an element.
 */
interface GridLocation {
    row: number;
    column: number;
}

interface ElementPosition {
    sectionId: SectionSpecId;
    location: GridLocation;
}

interface ElementPlaceholderPosition {
    positionAfterDraggedElementIsRemoved: ElementPosition;
}

type PlaceholderPositionAndRender =
    | {
          type: "BUTTON_PLACEHOLDER";
          position: ElementPlaceholderPosition;
          render: () => React.ReactElement;
      }
    | {
          type: "ELEMENT_PLACEHOLDER";
          position: ElementPlaceholderPosition;
          render: () => React.ReactElement;
      };

interface SectionPlaceholderPosition {
    indexAfterDraggedSectionRemoved: number;
}

interface SectionPlaceholderPositionAndRender<SEC> {
    position: SectionPlaceholderPosition;
    draggedSectionIndex: number;
    render: (
        sectionSpec: SEC,
        children: React.ReactElement | null
    ) => React.ReactElement;
}

interface DraggedButton<ElementButtonSpecId extends string> {
    type: "BUTTON";
    id: ElementButtonSpecId;
}

const ITEM_TYPES = {
    BUTTON: "BUTTON" as const,
    SECTION: "SECTION" as const,
    ELEMENT: "ELEMENT" as const,
};
export type {
    IconSpec,
    ElementButtonSpec,
    ElementAccessors,
    InternalElementAccessors,
    SectionAccessors,
    ElementSpecId,
    SectionSpecId,
    GridLocation,
    ElementPosition,
    ElementPlaceholderPosition,
    PlaceholderPositionAndRender,
    SectionPlaceholderPosition,
    SectionPlaceholderPositionAndRender,
    DraggedButton,
};
export { ITEM_TYPES };
